// cduUpload.ts -- Chunked Direct Upload (CDU) client driver for files
// >10 MB.
//
// The flow (mirrors POC raindb-cli ops/fdn.go::FdnDocumentUploadDirect
// L1006-1150 + raindb-app server/index.js L3422-3467):
//
//   1. POST /api/p/<pid>/documents/reserve with metadata
//      ({ folderId, filename, contentType, fileSize }).
//      BFF validates, calls phoenix's reserveDirectUpload mutation,
//      gets back { uploadUrl, dropletId, scopeValue }, inserts the
//      catalog entry with attrs.uploadStatus='uploading' so the icon
//      appears immediately. Returns the reservation to the client.
//
//   2. Browser PUTs the bytes DIRECTLY to S3 via the presigned URL.
//      No BFF involvement, no proxy. The XHR's upload.onprogress
//      drives the upload-progress store; the digest-status panel
//      reads from there to render a progress bar in the synthesised
//      "upload" stage.
//
//      Critical headers (per POC L1125-1126):
//        Content-Type: <same value passed to /reserve>
//        x-amz-content-sha256: UNSIGNED-PAYLOAD
//      The presign was minted with content-type binding -- mismatched
//      Content-Type returns 403 SignatureDoesNotMatch from S3. The
//      sha256 header is required for SigV4 presigns where the body
//      hash isn't known at presign time.
//
//      DO NOT add Authorization: Bearer here. The presigned URL
//      carries auth via query string; an Authorization header on top
//      breaks the signature.
//
//   3. POST /api/p/<pid>/documents/<docId>/commit on success. Tells
//      the BFF the upload phase is done; BFF clears the
//      uploadStatus='uploading' marker on the catalog entry so the
//      desktop icon stops showing the in-flight overlay.
//
//   4. Phoenix's S3-event-driven CDU completion handler runs
//      asynchronously when the object lands in staging. It moves
//      staging -> float, deletes staging, and triggers post-upload
//      flows subject to disabledFlows from /reserve. The client never
//      polls for this -- the existing /status endpoint surfaces the
//      digest pipeline state from the relay tree once the action
//      starts.
import { useUploadProgressStore } from '../state/uploadProgressStore';
import { useDocumentStatusStore } from '../state/documentStatusStore';
import { api, ApiError } from './api';
/**
 * Drive a CDU upload from start to finish. Resolves once the PUT
 * succeeds AND /commit fires; rejects on any failure (reserve, PUT,
 * or commit).
 *
 * Side effects (intentional -- the panel must reflect activity even
 * if the caller doesn't await this):
 *   - Opens a digest-status panel entry for the documentId so the
 *     user sees the upload immediately.
 *   - Drives the upload-progress store from the XHR upload events.
 *
 * The caller's responsibility is just to call this for files that
 * are over the inline-cap threshold; the helper handles everything
 * else.
 */
export async function uploadFileLargeCDU(projectId, folderId, file) {
    const filename = file.name;
    const contentType = file.type || 'application/octet-stream';
    const fileSize = file.size;
    // Compact, identifiable log prefix per upload so multi-file
    // uploads don't interleave incomprehensibly in the console. Each
    // call gets a timestamp-suffixed tag; subsequent log lines for
    // the same upload share the tag so the developer can grep one
    // upload's full lifecycle out of the console with one search.
    const tag = `[cdu:${filename.slice(0, 24)}@${Date.now() % 1_000_000}]`;
    // eslint-disable-next-line no-console
    console.log(`${tag} START`, {
        filename,
        contentType,
        fileSize,
        fileSizeMB: (fileSize / 1024 / 1024).toFixed(2),
        projectId,
        folderId,
    });
    const status = useDocumentStatusStore.getState();
    const progress = useUploadProgressStore.getState();
    // 1. /reserve -- BFF validates, mints presigned URL, inserts catalog
    // entry. We can't call openWindow before this since we don't have a
    // documentId yet. Once we do, both the panel + the progress store
    // are initialised before the PUT starts so the user sees the upload
    // appear in the panel immediately.
    let reservation;
    const reserveStart = performance.now();
    try {
        reservation = await api.post(`/p/${projectId}/documents/reserve`, { folderId, filename, contentType, fileSize });
        const reserveMs = Math.round(performance.now() - reserveStart);
        // eslint-disable-next-line no-console
        console.log(`${tag} RESERVE ok in ${reserveMs}ms`, {
            documentId: reservation.documentId,
            dropletId: reservation.dropletId,
            // Strip the signature query string so logs aren't a noisy mess;
            // host + path + key params are enough to identify the bucket
            // and target. The full signed URL is preserved on the
            // reservation object for the actual PUT.
            uploadUrlHost: new URL(reservation.uploadUrl).host,
            uploadUrlPath: new URL(reservation.uploadUrl).pathname,
            expiresAt: reservation.expiresAt,
            postUploadFlows: reservation.postUploadFlows.map((f) => ({
                flowId: f.flowId,
                enabled: f.enabled,
            })),
        });
    }
    catch (err) {
        const reserveMs = Math.round(performance.now() - reserveStart);
        const msg = err instanceof ApiError ? err.message : err.message;
        // eslint-disable-next-line no-console
        console.error(`${tag} RESERVE failed in ${reserveMs}ms`, {
            error: msg,
            status: err instanceof ApiError ? err.status : undefined,
            body: err instanceof ApiError ? err.body : undefined,
        });
        throw new Error(`CDU reserve failed: ${msg}`);
    }
    const { documentId, uploadUrl } = reservation;
    // Open the panel entry + progress store entry. From here on, every
    // upload-progress event the XHR fires is reflected in the panel.
    //
    // Capture pendingFlows from the reservation so the panel can
    // render queued placeholder stages BEFORE the server-side
    // digest dispatches. Defensively narrow the wire shape -- a
    // missing/empty postUploadFlows field is normal for formations
    // that don't declare any onDroplet actions.
    const pendingFlows = (reservation.postUploadFlows ?? []).map((f) => ({
        flowId: f.flowId,
        displayName: f.displayName,
        enabled: f.enabled,
        stages: (f.stages ?? []).map((s) => ({
            name: s.name,
            displayName: s.displayName,
        })),
    }));
    status.openWindow(documentId, filename, 'panel');
    progress.startUpload({
        documentId,
        filename,
        totalBytes: fileSize,
        pendingFlows,
    });
    // 2. Direct PUT to S3 via XHR for upload.onprogress reporting
    // (fetch() doesn't expose upload progress in browsers). Wrapped in
    // a Promise so we can await alongside the rest of the pipeline.
    const putStart = performance.now();
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        // Content-Type binding (presign was minted with this exact value).
        xhr.setRequestHeader('Content-Type', contentType);
        // SigV4 presign requires this header when the body isn't hashed
        // at presign time. Matches POC client (raindb ops/fdn.go:1126).
        xhr.setRequestHeader('x-amz-content-sha256', 'UNSIGNED-PAYLOAD');
        // Throttle progress logs so we don't flood the console: emit
        // every ~5% or every 5s, whichever comes first. The progress
        // store update fires on every event regardless; this is purely
        // a developer-visibility throttle.
        let lastLogPct = -1;
        let lastLogAt = 0;
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                progress.updateProgress(documentId, e.loaded);
                const pct = (e.loaded / e.total) * 100;
                const now = performance.now();
                if (pct - lastLogPct >= 5
                    || now - lastLogAt >= 5000
                    || e.loaded === e.total) {
                    lastLogPct = pct;
                    lastLogAt = now;
                    // eslint-disable-next-line no-console
                    console.log(`${tag} PUT progress`, {
                        loaded: e.loaded,
                        total: e.total,
                        pct: pct.toFixed(1) + '%',
                        elapsedMs: Math.round(now - putStart),
                        throughputMBps: e.loaded > 0
                            ? ((e.loaded / 1024 / 1024) / ((now - putStart) / 1000)).toFixed(2)
                            : '0',
                    });
                }
            }
            else {
                // eslint-disable-next-line no-console
                console.warn(`${tag} PUT progress event without lengthComputable`, e);
            }
        };
        xhr.onloadstart = () => {
            // eslint-disable-next-line no-console
            console.log(`${tag} PUT loadstart`);
        };
        xhr.onload = () => {
            const putMs = Math.round(performance.now() - putStart);
            if (xhr.status >= 200 && xhr.status < 300) {
                // eslint-disable-next-line no-console
                console.log(`${tag} PUT ok HTTP ${xhr.status} in ${putMs}ms`, {
                    avgThroughputMBps: ((fileSize / 1024 / 1024) / (putMs / 1000)).toFixed(2),
                    etag: xhr.getResponseHeader('ETag'),
                });
                progress.markComplete(documentId);
                resolve();
            }
            else {
                // S3 returns errors as XML; surface the body so the developer
                // sees the AWS error code (NoSuchBucket, AccessDenied,
                // SignatureDoesNotMatch, etc) directly in the console.
                const body = xhr.responseText ?? '';
                // eslint-disable-next-line no-console
                console.error(`${tag} PUT failed HTTP ${xhr.status} in ${putMs}ms`, {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseHeaders: xhr.getAllResponseHeaders(),
                    body: body.slice(0, 2000),
                });
                const errMsg = `S3 PUT failed: HTTP ${xhr.status} ${xhr.statusText}${body ? ` -- ${body.slice(0, 200)}` : ''}`;
                progress.markFailed(documentId, errMsg);
                reject(new Error(errMsg));
            }
        };
        xhr.onerror = () => {
            const putMs = Math.round(performance.now() - putStart);
            // xhr.onerror fires for ANY of: CORS preflight rejection,
            // DNS failure, connection refused, TLS error, browser
            // network-offline. The XHR itself doesn't tell us which --
            // by design (the browser hides those distinctions for
            // security). The console.error tells the developer to check
            // DevTools Network panel for the underlying status; the
            // error message returned to the panel stays clean.
            // eslint-disable-next-line no-console
            console.error(`${tag} PUT errored in ${putMs}ms`, {
                uploadUrlHost: (() => {
                    try {
                        return new URL(uploadUrl).host;
                    }
                    catch {
                        return '?';
                    }
                })(),
                hint: 'Open DevTools Network tab and find the PUT to that host. '
                    + 'Common causes: (1) S3 bucket missing CORS policy for the '
                    + 'browser origin, (2) presigned URL expired, (3) bucket '
                    + 'name typo / does not exist, (4) origin not in CORS '
                    + 'AllowedOrigins. Console > Network tab usually has a '
                    + 'red error log explaining which one.',
            });
            const errMsg = `Upload to S3 failed: connection error. See browser console for details.`;
            progress.markFailed(documentId, errMsg);
            reject(new Error(errMsg));
        };
        xhr.onabort = () => {
            const putMs = Math.round(performance.now() - putStart);
            // eslint-disable-next-line no-console
            console.warn(`${tag} PUT aborted after ${putMs}ms`);
            const errMsg = 'Upload aborted';
            progress.markFailed(documentId, errMsg);
            reject(new Error(errMsg));
        };
        xhr.ontimeout = () => {
            const putMs = Math.round(performance.now() - putStart);
            // eslint-disable-next-line no-console
            console.error(`${tag} PUT timed out after ${putMs}ms`, { xhrTimeout: xhr.timeout });
            const errMsg = 'Upload to S3 timed out';
            progress.markFailed(documentId, errMsg);
            reject(new Error(errMsg));
        };
        xhr.send(file);
    });
    // 3. /commit -- tells BFF the upload phase succeeded so the catalog
    // entry's uploadStatus marker can be cleared. Phoenix's S3-event-
    // driven CDU completion runs INDEPENDENTLY of this; /commit is just
    // a UI signal so the desktop icon stops showing the in-flight
    // overlay. If /commit fails we still consider the upload itself
    // succeeded -- the bytes are in S3, phoenix will complete eventually.
    const commitStart = performance.now();
    try {
        await api.post(`/p/${projectId}/documents/${documentId}/commit`, { folderId });
        const commitMs = Math.round(performance.now() - commitStart);
        // eslint-disable-next-line no-console
        console.log(`${tag} COMMIT ok in ${commitMs}ms`);
    }
    catch (err) {
        const commitMs = Math.round(performance.now() - commitStart);
        // eslint-disable-next-line no-console
        console.warn(`${tag} COMMIT failed in ${commitMs}ms (non-fatal, S3 upload already succeeded)`, {
            error: err instanceof ApiError ? err.message : err.message,
            status: err instanceof ApiError ? err.status : undefined,
            body: err instanceof ApiError ? err.body : undefined,
        });
    }
    // eslint-disable-next-line no-console
    console.log(`${tag} DONE`, { documentId, fileSize, filename });
    return { documentId, filename, fileSize };
}
/**
 * The threshold AT-OR-BELOW which uploads take the inline multipart
 * path (POST /documents); files ABOVE this size go through CDU.
 *
 * As of the Lightning Bolt port: set to 0. Every upload (any size,
 * any byte count, including the 1-byte edge case) takes CDU. The
 * inline path is dead code in this deployment. The per-file ceiling
 * is whatever S3 enforces for a single PUT (5 TiB single-object,
 * larger with multipart upload -- and the browser File API can't
 * reach either limit anyway).
 *
 * Why everything-through-CDU under the bolt:
 *
 *   - The goja sandbox exposes request bodies as JS strings via
 *     Go's `string([]byte)` conversion, which UTF-8-decodes any
 *     bytes >= 0x80 and replaces invalid sequences with U+FFFD.
 *     Binary multipart bodies (PDFs, images, anything non-ASCII)
 *     get corrupted before the bolt code can parse them. An inline
 *     path would either need a Go-side base64 primitive on the
 *     lightning ctx OR a Go-side multipart parser exposed to the
 *     sandbox; both push fdn-app-shaped concerns into the lightning
 *     runtime, which is the wrong layer.
 *
 *   - Lightning's per-request body cap defaults to 1 MB. Inline
 *     uploads in the 1-10 MB range would 413 at the sandbox gate
 *     even if the body-string conversion were lossless. Raising
 *     the cap to accommodate large bodies has memory-pressure
 *     implications for the lightning service (every concurrent
 *     bolt request buffers its full body in Go before invoking
 *     the sandbox).
 *
 *   - CDU sidesteps both: the browser PUTs bytes DIRECTLY to S3
 *     via a presigned URL minted by the bolt's /reserve handler.
 *     Bytes never transit the bolt sandbox; /reserve + /commit
 *     see only small JSON payloads (well under any body cap).
 *     S3 enforces the actual size ceiling (5 TiB single-PUT,
 *     larger with multipart -- we're nowhere near either).
 *
 * Trade-off: 3 round-trips (reserve + S3 PUT + commit) instead of
 * a single multipart POST. Each round-trip is ~20-50ms; the extra
 * latency vs inline is imperceptible for the user. The architectural
 * benefit (no binary-string corruption, no sandbox-memory pressure,
 * no body-cap negotiation, no multipart parser in the bolt) is
 * worth it.
 *
 * If the express server is ever restored as the primary backend and
 * the bolt is retired, this can be reset to 10 * 1024 * 1024 to
 * match the express inline-path cap.
 */
export const INLINE_UPLOAD_MAX_BYTES = 0;
